import { Component, ViewEncapsulation, OnDestroy, Inject } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';

import { Subscription } from 'rxjs';
import { SnackBarComponentType } from '../models/snackbar';

/**
 * Application default snackbar which shows a message and action
 *
 * @export
 * @class SnackbarComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @implements {AfterViewInit}
 * @implements {SnackBarComponentType}
 */
@Component({
  selector: 'app-snackbar',
  templateUrl: './snackbar.component.html',
  styleUrls: ['./snackbar-default.raw.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SnackbarComponent implements OnDestroy, SnackBarComponentType {
  type: string;
  message: string;
  actionLabel: string;
  hasAction: boolean;
  data: any;

  subDismiss: Subscription;
  subOpened: Subscription;
  subAction: Subscription;

  /**
   * Creates an instance of SnackbarComponent.
   *
   * @param {MatSnackBarRef<SnackBarComponentType>} snackBarRef
   * @param {*} data
   * @memberof SnackbarComponent
   */
  constructor(
    public snackBarRef: MatSnackBarRef<SnackBarComponentType>,
    @Inject(MAT_SNACK_BAR_DATA) data: any
  ) {
    this.data = data;
    this.type = data.type;
    this.message = data.props['message'];
    this.actionLabel = data.props['button'] ? data.props['button'].label : undefined;
    this.hasAction = !!data.props['button'];
    this.addSubscribers();
  }

  /**
   * Adds the subscribers for afterDismisses, afterOpened and onAction
   *
   * @private
   * @memberof SnackbarComponent
   */
  private addSubscribers(): void {
    // Snackbar on dismissed forcefully or timeout
    this.subDismiss = this.snackBarRef.afterDismissed().subscribe(() => {
      if (this.subDismiss) {
        this.subDismiss.unsubscribe();
      }
      const afterDismiss = this.data.props['afterDismissCallback'];
      if (afterDismiss) {
        afterDismiss();
      }
    });

    this.subOpened = this.snackBarRef.afterOpened().subscribe(() => {
      const afterOpen = this.data.props['afterOpenCallback'];
      if (afterOpen) {
        afterOpen();
      }
    });

    this.subAction = this.snackBarRef.onAction().subscribe(() => {
      const action = this.data.props['button'].action;
      if (action) {
        action();
      }
    });
  }

  /**
   * Invoked from the markup when the action button is clicked
   *
   * @memberof SnackbarComponent
   */
  action(): void {
    this.snackBarRef.dismissWithAction();
  }

  /**
   * Invoked to close the snackbar
   *
   * @memberof SnackbarComponent
   */
  close(): void {
    this.snackBarRef.dismiss();
  }

  /**
   * Unsubscribes the subscribers on component destroy
   *
   * @memberof SnackbarComponent
   */
  ngOnDestroy() {
    this.subOpened.unsubscribe();
    this.subAction.unsubscribe();
  }
}
